import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import CustomerServices from "../../../services/OrderingServices/CustomerServices";

import { useEffect, useRef, useState } from "react";
import CardPaymentService from "../../../services/OrderingServices/CardPaymentService";
import Select from "react-select";

const CardAssignModal = ({
  isOpen,
  toggle,
  customerDetail,
  fetchCardAssign,
}) => {
  const inputRef = useRef(null);

  const [assignedCard, setAssignedCard] = useState(null); // track assigned card
  const [loading, setLoading] = useState(false);

  const [customerList, setCustomerList] = useState([]);
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await CustomerServices.getCustomerLists();
      setCustomerList(response.customers || []);

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);
  // Step 1: Assign Card Formik
  const assignFormik = useFormik({
    initialValues: {
      cardUid: "",
      userId: customerDetail?.customer?.id
        ? String(customerDetail.customer.id)
        : "",
      userName:
        customerDetail?.customer?.firstName +
        " " +
        customerDetail?.customer?.lastName,
      userType: 1,
      expiryDate: "",
      dailyLimit: 0,
      txLimit: 0,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      cardUid: Yup.string().required("Card UID is required"),
    }),
    onSubmit: async (values) => {
      try {
        const res = await CustomerServices.assignCard(values);
        toast.success("Card assigned successfully");
        setAssignedCard(values.cardUid); // save the card UID
      } catch (error) {
        console.log(error);
        toast.error(error?.response?.data?.message || "Failed to assign card");
      }
    },
  });

  // Step 2: Recharge Formik (only enabled after card assigned)
  const rechargeFormik = useFormik({
    initialValues: {
      cardUid: assignedCard || "",
      amount: 0,
      paymentMethod: 1,
      remarks: "",
    },
    enableReinitialize: true, // update cardUid when assigned
    validationSchema: Yup.object({
      cardUid: Yup.string().required("Card UID is required"),
      amount: Yup.number().required("Amount is required"),
      paymentMethod: Yup.number().required("Payment Method is required"),
      remarks: Yup.string().required("Remarks is required"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        await CardPaymentService.recharge(values);
        toast.success("Recharge successful");
        fetchCardAssign();
        toggle();
        setAssignedCard(null); // reset for next use
      } catch (error) {
        toast.error(error?.response?.data?.message);
      } finally {
        setLoading(false);
      }
    },
  });

  // Auto focus RFID input
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  const customerOptions = customerList?.map((customer) => ({
    value: String(customer.id),
    label: `${customer.firstName} ${customer.lastName}`,
  }));

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Assign & Recharge RFID Card</ModalHeader>
      <ModalBody>
        {/* Step 1: Assign Card */}
        {!assignedCard && (
          <form
            onSubmit={assignFormik.handleSubmit}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault(); // ❌ Prevent Enter from submitting
                // Optional: move focus to next input if needed
              }
            }}
          >
            <FormGroup className="d-flex flex-column align-items-center">
              <div
                style={{
                  width: "100%",
                  border: "px dashed #6c757d",
                  borderRadius: "12px",
                  padding: "20px",
                  textAlign: "center",
                  backgroundColor: assignFormik.values.cardUid
                    ? "#e6ffed"
                    : "#f8f9fa",
                  transition: "all 0.3s ease",
                  boxShadow: assignFormik.values.cardUid
                    ? "0 0 10px rgba(40, 167, 69, 0.5)"
                    : "0 0 5px rgba(108, 117, 125, 0.2)",
                }}
              >
                <Input
                  innerRef={inputRef}
                  id="cardUid"
                  name="cardUid"
                  type="text"
                  placeholder="Tap your card here..."
                  autoComplete="off"
                  value={assignFormik.values.cardUid}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\n/g, "").trim();
                    assignFormik.setFieldValue("cardUid", value);
                  }}
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "1.2rem",
                    border: "none",
                    outline: "none",
                    background: "transparent",
                  }}
                />
              </div>
              <div style={{ marginTop: "10px", fontWeight: "500" }}>
                {!assignFormik.values.cardUid ? (
                  <span style={{ color: "#6c757d" }}>
                    👉 Please tap your RFID card
                  </span>
                ) : (
                  <span style={{ color: "green" }}>✅ Card Detected</span>
                )}
              </div>
              <FormFeedback>{assignFormik.errors.cardUid}</FormFeedback>
            </FormGroup>
            {customerDetail?.customer?.id ? null : (
              <FormGroup>
                <Label>Customer</Label>
                <Select
                  name="userId"
                  value={
                    customerOptions.find(
                      (option) => option.value === assignFormik.values.userId,
                    ) || null
                  } // sync Formik value
                  onChange={(selectedOption) => {
                    assignFormik.setFieldValue(
                      "userId",
                      selectedOption ? selectedOption.value : "",
                    );
                    assignFormik.setFieldValue(
                      "userName",
                      selectedOption ? selectedOption.label : "",
                    );
                  }}
                  options={customerOptions}
                  placeholder="Select Customer"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (baseStyles) => ({
                      ...baseStyles,
                      padding: "0.1rem",
                    }),
                  }}
                />
              </FormGroup>
            )}
            <FormGroup>
              <Label>User Type</Label>
              <Input
                type="select"
                name="userType"
                value={assignFormik.values.userType}
                onChange={(e) =>
                  assignFormik.setFieldValue("userType", Number(e.target.value))
                }
              >
                <option value={1}>Student</option>
                <option value={2}>Teacher</option>
                <option value={3}>Staff Member</option>
              </Input>
            </FormGroup>

            <FormGroup>
              <Label>Expiry Date</Label>
              <Input
                type="date"
                {...assignFormik.getFieldProps("expiryDate")}
              />
            </FormGroup>

            <FormGroup>
              <Label>Daily Limit</Label>
              <Input
                type="number"
                {...assignFormik.getFieldProps("dailyLimit")}
              />
            </FormGroup>

            <FormGroup>
              <Label>Transaction Limit</Label>
              <Input type="number" {...assignFormik.getFieldProps("txLimit")} />
            </FormGroup>

            <ModalFooter>
              <Button color="danger" onClick={toggle}>
                Cancel
              </Button>
              <Button color="success" type="submit" disabled={loading}>
                Assign Card
              </Button>
            </ModalFooter>
          </form>
        )}

        {/* Step 2: Recharge Card */}
        {assignedCard && (
          <form onSubmit={rechargeFormik.handleSubmit}>
            <FormGroup>
              <Label for="amount">Amount</Label>
              <Input
                id="amount"
                name="amount"
                type="number"
                {...rechargeFormik.getFieldProps("amount")}
              />
              <FormFeedback>{rechargeFormik.errors.amount}</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label for="remarks">Remarks</Label>
              <Input
                id="remarks"
                name="remarks"
                type="textarea"
                {...rechargeFormik.getFieldProps("remarks")}
              />
              <FormFeedback>{rechargeFormik.errors.remarks}</FormFeedback>
            </FormGroup>

            <ModalFooter>
              <Button color="danger" onClick={() => setAssignedCard(null)}>
                Back
              </Button>
              <Button color="success" type="submit" disabled={loading}>
                Recharge Card
              </Button>
            </ModalFooter>
          </form>
        )}
      </ModalBody>
    </Modal>
  );
};

export default CardAssignModal;
